// @generated by `pnpm content:generate` from src/content-export — DO NOT EDIT
import type { TravelerPack } from '../types.js'

export const FR: TravelerPack = {
  jurisdiction: {
    id: 'FR',
    type: 'country',
    name: 'France',
  },
  metros: [
    {
      id: 'FR-PAR',
      type: 'metro',
      name: 'Paris',
      parent: 'FR',
    },
  ],
  quickFacts: {
    driveSide: 'right',
    motorwayLimitKmh: 130,
    alcoholLimit: '0.05% (0.02 novice)',
    vignette: 'None (tolls)',
    emergency: '112',
  },
  rules: [
    {
      id: 'fr-critair',
      target: 'FR-PAR',
      category: 'zone',
      severity: 'high',
      title: 'Crit\'Air low-emission zones (ZFE)',
      detail: 'Paris, Lyon and other cities require a Crit\'Air sticker to enter low-emission zones; driving without one risks a fine.',
      cost: '€68+',
      citation: 'https://www.certificat-air.gouv.fr/en/',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'fr-antai',
      target: 'FR',
      category: 'document',
      severity: 'high',
      title: 'Fines routed via ANTAI to your home address',
      detail: 'Speed/zone fines are forwarded internationally through ANTAI and your rental company; pay promptly to avoid escalation.',
      cost: 'escalates',
      citation: 'https://www.antai.gouv.fr/',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'fr-equipment',
      target: 'FR',
      category: 'equipment',
      severity: 'medium',
      title: 'Hi-vis vest + warning triangle',
      detail: 'Carry a reflective vest (within reach) and a warning triangle; wear the vest if you exit on the roadside.',
      citation: 'https://www.service-public.fr/particuliers/vosdroits/F2837',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'fr-vignette',
      target: 'FR',
      category: 'vignette_toll',
      severity: 'info',
      title: 'No vignette (autoroute tolls)',
      detail: 'France uses per-trip autoroute tolls rather than a vignette.',
      cost: '€/km',
      citation: 'https://www.autoroutes.fr/en/',
      lastReviewed: '2026-06-02',
    },
  ],
  checklists: {
    pre: [
      {
        id: 'fr-pre-critair',
        label: 'Order your Crit\'Air sticker',
        detail: 'If your trip touches Paris, Lyon or other ZFEs you need a Crit\'Air sticker; order it early as it ships by post.',
        citation: 'https://www.certificat-air.gouv.fr/en/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-pre-equipment',
        label: 'Confirm hi-vis vest + warning triangle',
        detail: 'French law requires a reflective vest within reach and a warning triangle; check the rental has both.',
        citation: 'https://www.service-public.fr/particuliers/vosdroits/F2837',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-pre-tolls',
        label: 'Set up an autoroute toll + parking option',
        detail: 'Plan for péage tolls (a card or télépéage tag) and a city parking app to avoid fumbling at booths.',
      },
      {
        id: 'fr-pre-offline',
        label: 'Download the offline city + ZFE pack',
        detail: 'Save offline maps and the low-emission-zone boundaries for the cities you will enter.',
      },
    ],
    on: [
      {
        id: 'fr-on-critair',
        label: 'Display the Crit\'Air sticker before entering a ZFE',
        detail: 'Driving into a low-emission zone without a valid sticker risks a fine.',
        citation: 'https://www.certificat-air.gouv.fr/en/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-on-equipment',
        label: 'Keep the vest reachable; vest on before you exit',
        detail: 'Put the reflective vest on inside the car before stepping onto the roadside, and set the triangle behind you.',
        citation: 'https://www.service-public.fr/particuliers/vosdroits/F2837',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-on-docs',
        label: 'Keep licence, IDP and passport reachable',
        detail: 'Carry the original documents for roadside checks.',
      },
    ],
    post: [
      {
        id: 'fr-post-antai',
        label: 'Watch for an ANTAI fine notice',
        detail: 'Speed and zone fines are forwarded to your home address via ANTAI and the rental company; check proactively.',
        citation: 'https://www.antai.gouv.fr/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-post-pay',
        label: 'Pay inside the reduced-amount window',
        detail: 'French fines are cheaper if paid promptly and escalate if ignored — do not let rental admin delays push you past the window.',
        citation: 'https://www.antai.gouv.fr/',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'fr-post-dispute',
        label: 'Know your dispute rights',
        detail: 'You can contest a fine through ANTAI; keep rental dates and photos as evidence.',
      },
    ],
  },
  course: {
    title: 'Driving Abroad Confidently',
    modules: [
      {
        id: 'fr-m1-before',
        title: 'Before You Go',
        minutes: 5,
        body: 'France is straightforward to drive if you handle two paperwork items early. First, the Crit\'Air sticker: many cities run low-emission zones (ZFE) and you need the sticker to enter, ordered in advance because it ships by post. Second, mandatory equipment — a reflective vest within reach and a warning triangle, both of which French law expects in the car. Non-EU drivers should carry an International Driving Permit with their national licence. Set up a way to pay autoroute tolls and city parking, and download offline maps plus the ZFE boundaries for the cities on your route so nothing catches you mid-trip.',
        keyPoints: [
          'Order the Crit\'Air sticker early — it ships by post',
          'Carry a hi-vis vest and warning triangle',
          'Bring an IDP; plan tolls, parking and offline maps',
        ],
      },
      {
        id: 'fr-m2-car',
        title: 'The Car & The Side',
        minutes: 5,
        body: 'France drives on the right, with autoroute limits up to 130 km/h in the dry, dropping to 110 in the wet. Keep right except to overtake; the left lane is for passing only and police enforce it. Priority rules can surprise visitors — at some unmarked junctions the older priorité à droite (give way to the right) still applies, so read the signs. Roundabouts are common and usually give priority to traffic already on the ring. Spend a few minutes in the rental lot finding the lights, wipers and hazard switch, and confirm where the mandatory vest and triangle are stored before you set off.',
        keyPoints: [
          'Drive on the right; 130 dry / 110 wet on the autoroute',
          'Keep right except to overtake',
          'Mind priorité à droite at unmarked junctions',
        ],
      },
      {
        id: 'fr-m3-surprise',
        title: 'Rules That Surprise You',
        minutes: 5,
        body: 'The surprises in France are zones and equipment, not just speed. Crit\'Air low-emission zones in Paris, Lyon and a growing list of cities require a windscreen sticker; enter without one and you risk a fine even if your car is clean. The mandatory reflective vest must be reachable from your seat — not in the boot — and you must wear it if you step onto the roadside. Alcohol limits are strict, lower still for novice drivers. None of these are about how fast you go, which is exactly why confident drivers overlook them. Treat the sticker and the vest as non-negotiable trip items.',
        keyPoints: [
          'Crit\'Air sticker needed to enter low-emission zones',
          'Reflective vest must be within reach, not in the boot',
          'Strict alcohol limits, lower for novices',
        ],
      },
      {
        id: 'fr-m4-fines',
        title: 'The Hidden Fine Traps',
        minutes: 5,
        body: 'France has an efficient system for reaching foreign drivers: ANTAI. Speed-camera and zone fines are forwarded internationally to your home address, usually after your rental company hands over your details and adds an admin fee. The trap is timing and inertia. Fines are cheaper if paid promptly and escalate if ignored, but the notice can arrive weeks later, eating into the reduced-amount window. Do not assume distance protects you — ANTAI does cross borders. Check proactively, pay valid fines quickly, and if a fine is wrong — wrong date, not the driver — dispute it through ANTAI with your rental dates and photos.',
        keyPoints: [
          'ANTAI forwards fines to your home address abroad',
          'Pay promptly — fines escalate if ignored',
          'Dispute genuine errors through ANTAI with evidence',
        ],
        check: {
          q: 'A French speed-camera fine can be forwarded to your home address abroad through ANTAI. True or false?',
          options: [
            {
              label: 'True — ANTAI reaches foreign drivers',
              correct: true,
            },
            {
              label: 'False — fines stop at the border',
              correct: false,
            },
          ],
        },
      },
      {
        id: 'fr-m5-wrong',
        title: 'When Things Go Wrong',
        minutes: 5,
        body: 'In an emergency dial 112, which reaches every service. After a collision, move to safety, switch on hazards, put on your reflective vest before stepping out, and place the warning triangle well behind the car. Exchange details using the constat amiable — the European Accident Statement — a standard bilingual form your insurer provides; fill it in jointly rather than trusting memory. Photograph positions, plates and damage. Do not admit liability at the roadside; let the form and the insurers settle it. For injuries, theft or any dispute, wait for the authorities and keep your licence, IDP and rental papers to hand.',
        keyPoints: [
          'Dial 112 for all emergencies',
          'Vest on, then triangle, before you exit',
          'Use the constat amiable; photograph everything',
        ],
      },
      {
        id: 'fr-m6-applied',
        title: 'Your Trip Applied',
        minutes: 5,
        body: 'Put it together for a France trip. Before leaving home: order the Crit\'Air sticker, confirm the vest and triangle, bring your IDP, and set up tolls and offline maps. On the ground: display the sticker before any low-emission zone, keep the vest reachable, and keep right except to overtake. Day to day: respect the dry/wet limits, watch for priorité à droite, and stay under the alcohol limit. After you return: check for an ANTAI notice, pay inside the reduced-amount window, and dispute genuine errors with evidence. Handle the sticker and the equipment up front and France is an easy, rewarding drive.',
        keyPoints: [
          'Pre-trip: Crit\'Air, vest + triangle, IDP, tolls',
          'On-trip: sticker before ZFEs, keep right, mind priority',
          'Post-trip: handle the ANTAI notice promptly',
        ],
      },
    ],
  },
  emergency: {
    number: '112',
    steps: [
      {
        title: 'After an accident',
        detail: 'Stop, move to safety, switch on hazards and check everyone. Call 112 for injuries; give your location and number of people hurt.',
      },
      {
        title: 'European Accident Statement',
        detail: 'Complete the constat amiable / European Accident Statement with the other driver — it is standardised and bilingual, so use it instead of free-form notes.',
      },
      {
        title: 'Hazard signal + triangle',
        detail: 'Put on your reflective vest before leaving the car and place the warning triangle well back up the road to warn approaching traffic.',
      },
    ],
    phrases: [
      {
        en: 'I need an ambulance',
        local: 'J\'ai besoin d\'une ambulance',
      },
      {
        en: 'I don\'t understand',
        local: 'Je ne comprends pas',
      },
      {
        en: 'Where is the nearest hospital?',
        local: 'Où est l\'hôpital le plus proche ?',
      },
    ],
  },
}
